<script setup lang="ts">
const { t } = useI18n();
const { getCollectibleById } = useCollectiblesController();
const appStateStore = useAppStateStore();
const route = useRoute();
const title = ref<string>("");
const imageUrl = ref<string>("");
const localePath = useLocalePath();

useSeoMeta({
  title: () => title.value,
  description: `${t("main.marketplaceSEO.description.main")}`,
  ogType: "website",
  ogTitle: () => title.value,
  ogDescription: `${t("main.marketplaceSEO.description.main")}`,
  ogUrl: "https://gfal.market",
  ogImage: () => imageUrl.value,
  twitterCard: "summary_large_image",
  twitterSite: "@GFAL_Official",
  twitterTitle: () => title.value,
  twitterDescription: `${t("main.marketplaceSEO.description.main")}`,
  twitterImage: () => imageUrl.value,
});

const setSEO = (collectible: Collectible) => {
  title.value = `${t(`${collectible.translationId}.title`)} ${t(
    `${collectible.translationId}.subtitle`
  )} #${collectible.number}`;
  imageUrl.value = getPortraitItemImage(collectible.imageId).replace(
    "webp",
    "png"
  );
};

const navigateBack = () => {
  if (collectible.value?.gfalId) {
    navigateTo(localePath(`/user/${collectible.value.gfalId}`));
  } else {
    navigateTo(localePath("/"));
  }
};

const { data: collectible, status } = await useAsyncData(
  "getMarketCollectibleById",
  async () => {
    const collectible = await getCollectibleById(route.params.id as string);
    if (collectible) {
      appStateStore.currentMerchant = collectible.merchantId as MerchantId;
      await appStateStore.getTranslationsFile(collectible.merchantId);
    }
    return collectible;
  },
  {
    default() {
      return null;
    },
    lazy: true,
  }
);

watch(
  collectible,
  (newCollectible) => {
    if (newCollectible) setSEO(newCollectible);
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="collectible && collectible.gfalId"
    class="flex items-center mb-7 cursor-pointer text-neutral-300 hover:text-primary-500"
    @click="navigateBack">
    <NuxtIcon
      name="mdi:chevron-left"
      class="h-5 w-5 shrink-0"
      aria-hidden="true" />
    <p class="text-xl font-bold">
      {{ $t("main.userCollectionDetail.backToList") }}
    </p>
  </div>
  <div
    v-if="status === 'pending' || !useAppStateStore().areTidsLoaded"
    class="flex items-center justify-center h-96">
    <LoaderSpinner size="2xl" />
  </div>
  <CardCollectibleDetail
    v-else-if="status === 'success' && collectible"
    :collectible="collectible" />
  <CommonNotFoundItem v-else />
</template>
